<script setup lang="ts">
import { createRandomString } from "@/general_functions";
import { FormInput, FormNested, FormValues } from "../types";
import { formValuesToJSONData } from "../functions";

/////////////////////////////////////////////////////////////
////////////////////// PROPS /////////////////////////////
/////////////////////////////////////////////////////////////
const props = defineProps<{
  config: FormNested;
  modelValue: Record<string, any>;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: any): void;
}>();

/////////////////////////////////////////////////////////////
////////////////////// SETUP /////////////////////////////
/////////////////////////////////////////////////////////////
const { t } = useI18n();
const tab = ref<number>(0);
const values = ref<any[]>([getDefaultValues()]);

/////////////////////////////////////////////////////////////
////////////////////// COMPUTED /////////////////////////////
/////////////////////////////////////////////////////////////
const conditionalSections = computed(() => {
  return props.config.sections?.filter((section) => {
    if (section.conditional) {
      // Assess the conditional field
      return section.conditional(value.value);
    }
    return true;
  });
});

const value = computed({
  get: () => props.modelValue,
  set: (value: any) => {
    emit("update:modelValue", value);
  },
});

const multipleForms = computed(() => {
  return props.config.list || props.config.object;
});

/////////////////////////////////////////////////////////////
////////////////////// METHODS //////////////////////////////
/////////////////////////////////////////////////////////////
function conditionalSectionsForIndex(index: number) {
  return props.config.sections?.filter((section) => {
    if (section.conditional) {
      // Assess the conditional field
      return section.conditional(values.value[index]);
    }
    return true;
  });
}

function allFields(): FormInput[] {
  return props.config.sections?.map((section) => section.fields).flat() || [];
}

function getDefaultValues(): any {
  return {
    ...Object.fromEntries(
      allFields()
        .map((field) => [
          field.name,
          field.type !== "nested-form" &&
          field.type !== "button" &&
          field.type !== "table" &&
          field.type !== "custom"
            ? field.defaultValue
            : undefined,
        ])
        .filter((v) => v[1] !== undefined),
    ),
    key: createRandomString(10),
  };
}

function addTab() {
  values.value.push(getDefaultValues());
  tab.value = values.value.length - 1;
}

function tabTitle(index: number): string {
  if (props.config.labels) {
    return props.config.labels[index];
  } else {
    return `${props.config.title} ${index + 1}`;
  }
}

function panelTitle(index: number): string {
  if (props.config.title) {
    if (typeof props.config.title === "string") {
      return index + 1 + " - " + t(props.config.title);
    } else {
      const title = props.config.title(values.value[index]);
      if (typeof title === "string") {
        return index + 1 + " - " + t(title);
      }
    }
  }
  return (index + 1).toString();
}

/////////////////////////////////////////////////////////////
////////////////////// WATCHERS /////////////////////////////
/////////////////////////////////////////////////////////////

watch(
  () => value.value[props.config.name],
  (v) => {
    if (!props.config.object) {
      values.value = v;
    }
  },
  { deep: true },
);

watch(
  () => values.value,
  (v) => {
    if (props.config.object) {
      value.value[props.config.name] = Object.fromEntries(
        v.map((v) => [v.key, v]),
      );
    } else {
      value.value[props.config.name] = v;
    }
  },
  { deep: true },
);

/////////////////////////////////////////////////////////////
////////////////////// HOOKS /////////////////////////////
/////////////////////////////////////////////////////////////
onMounted(() => {
  if (props.config.object) {
    const data = formValuesToJSONData(value.value) as any;
    props.config.keys?.forEach((key, index) => {
      if (!data[props.config.name][key]) {
        values.value[index] = {
          ...getDefaultValues(),
          key,
        };
      } else {
        values.value[index] = {
          ...data[props.config.name][key],
          key,
        };
      }
    });
  } else if (!value.value[props.config.name]) {
    value.value[props.config.name] = [getDefaultValues()];
  } else {
    values.value = value.value[props.config.name];
  }
});
</script>

<template>
  <VRow class="pa-3">
    <!-- ACCORDION -->
    <VCol v-if="multipleForms && config.accordion" cols="12">
      <VExpansionPanels>
        <VExpansionPanel
          v-for="(v, index) in values"
          :key="index"
          :value="index"
        >
          <VExpansionPanelTitle>
            <h3>
              {{ panelTitle(index) }}
            </h3>
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <VRow>
              <VCol cols="12" class="d-flex justify-end">
                <VBtn
                  @click="
                    values.splice(index, 1);
                    tab = 0;
                  "
                  class="ml-1"
                  size="x-small"
                >
                  <VIcon icon="tabler-trash" />
                </VBtn>
              </VCol>
            </VRow>
            <FormSection
              v-for="section in conditionalSectionsForIndex(index)"
              :section="section"
              v-model="values[index]"
              :loading="loading"
            />
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <VRow>
        <VCol class="d-flex justify-center mt-5">
          <VBtn v-if="config.addable" @click="addTab" :disabled="loading">
            <VIcon size="24" icon="tabler-plus" />
            {{ $t("Add item") }}
          </VBtn>
        </VCol>
      </VRow>
    </VCol>

    <!-- TABS -->
    <VCol v-else-if="multipleForms" cols="12">
      <VTabs v-model="tab">
        <VTab v-for="(v, index) in values" :value="index">
          {{ tabTitle(index) }}
        </VTab>
        <VTab v-if="config.addable" :disabled="loading" @click="addTab">
          <VIcon size="24" icon="tabler-plus" />
        </VTab>
      </VTabs>
      <IconBtn
        v-if="config.addable"
        :disabled="loading"
        style="position: absolute; right: 20px; margin-top: 10px"
        @click="
          values.splice(tab, 1);
          tab = 0;
        "
      >
        <VIcon icon="tabler-trash" />
      </IconBtn>
    </VCol>

    <!--- TAB TITLE --->
    <VCol v-if="config.title && !config.accordion" cols="12">
      <h4>
        {{ config.title ? $t(config.title as string) : "" }} {{ tab + 1 }}
      </h4>
    </VCol>

    <VCol
      v-if="multipleForms && !config.accordion"
      cols="12"
      style="margin: 0 !important; padding: 0 !important"
    >
      <FormSection
        v-for="section in conditionalSectionsForIndex(tab)"
        :section="section"
        v-model="values[tab]"
        :loading="loading"
      />
    </VCol>

    <VCol v-else-if="!config.accordion" cols="12">
      <FormSection
        v-for="section in conditionalSections"
        :section="section"
        v-model="value"
      />
    </VCol>
  </VRow>
</template>
